<!-- surfaceClass() flips between a comfortable bordered modal and an edge-to-edge fullscreen view. -->
<hlm-dialog-surface size="xl" [class]="surfaceClass()">
  <hlm-dialog-header class="flex items-start gap-3 border-b border-border px-5 pb-4 pt-5">
    <hlm-dialog-icon-badge tone="primary">
      <hlm-icon name="lucidePhone" size="1.1rem" />
    </hlm-dialog-icon-badge>

    <div hlmDialogHeading class="min-w-0 flex-1">
      <!-- Title + CCHSID + Explain Routing on ONE row to save vertical space.
           The CCHSID pill is now a SHARE-menu trigger (EB-3807): clicking it
           opens Copy / Teams / Email / Open Graylog. The Explain Routing button
           (moved here from the Accounting tab in EB-3807) sits right beside it. -->
      <div class="flex flex-wrap items-center gap-3">
        <h2 hlmDialogTitle class="m-0">Call Details</h2>

        <!-- EB-3807: CCHSID share menu. align="start" so it opens under the pill
             (left-anchored), away from the top-right action cluster. -->
        <hlm-menu align="start" width="min-w-[15rem]">
          <button
            type="button"
            hlmMenuTrigger
            [attr.aria-label]="'Share CCHSID ' + data.cchsid"
            hlmTooltip="CCHSID: Call center handling session ID"
            [attr.title]="'CCHSID: Call center handling session ID'"
            class="font-mono-feat group inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted/60 py-0.5 pl-2.5 pr-1.5 text-sm text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-ring/40"
          >
            <span class="break-all">CCHSID: {{ data.cchsid }}</span>
            <!-- Share affordance: own pill so the icon reads as a distinct,
                 tappable share control rather than getting lost next to the ID. -->
            <span
              class="inline-flex flex-none items-center justify-center rounded-md bg-primary/10 px-2.5 py-1 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground"
            >
              <hlm-icon name="lucideShare2" size="0.95rem" />
            </span>
          </button>
          <ng-template hlmMenuContent>
            <div class="cmt-eyebrow px-3 pb-1.5 pt-2.5">Share CCHSID</div>
            <div class="flex flex-col gap-0.5 p-1.5">
              <button
                hlmMenuItem
                class="!gap-3 rounded-md !px-3 !py-2.5 !text-sm transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                (click)="copyCchsid()"
              >
                <hlm-icon name="lucideCopy" size="1rem" class="text-muted-foreground" />
                <span>Copy CCHSID</span>
              </button>
              <button
                hlmMenuItem
                class="!gap-3 rounded-md !px-3 !py-2.5 !text-sm transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                (click)="copyLink()"
              >
                <hlm-icon name="lucideLink" size="1rem" class="text-muted-foreground" />
                <span>Copy link</span>
              </button>
              <button
                hlmMenuItem
                class="!gap-3 rounded-md !px-3 !py-2.5 !text-sm transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                (click)="onShareTeams()"
              >
                <hlm-icon name="lucideMessageSquare" size="1rem" class="text-muted-foreground" />
                <span>Send via Teams…</span>
              </button>
              <button
                hlmMenuItem
                class="!gap-3 rounded-md !px-3 !py-2.5 !text-sm transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                (click)="onShareEmail()"
              >
                <hlm-icon name="lucideMail" size="1rem" class="text-muted-foreground" />
                <span>Email…</span>
              </button>
              <!-- EB-3807: Open Graylog — gated on the Technik department (see
                   component TODO). Enabled item opens the Graylog CCHSID search;
                   disabled state is a non-interactive row with a lock + hint so
                   the feature stays discoverable. -->
              @if (canOpenGraylog()) {
                <button
                  hlmMenuItem
                  class="!gap-3 rounded-md !px-3 !py-2.5 !text-sm transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                  (click)="onOpenGraylog()"
                >
                  <hlm-icon name="lucideScrollText" size="1rem" class="text-muted-foreground" />
                  <span>Open Graylog</span>
                </button>
              } @else {
                <div
                  class="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground opacity-70"
                  hlmTooltip="Requires the Technik department"
                  [attr.title]="'Requires the Technik department'"
                >
                  <hlm-icon name="lucideLock" size="1rem" class="text-muted-foreground" />
                  <span>Open Graylog</span>
                </div>
              }
            </div>
          </ng-template>
        </hlm-menu>

        <!-- EB-3807: Explain Routing — Routing Manager Call Explainer deep-link,
             moved next to the CCHSID (was the Accounting-tab toolbar in EB-3805).
             Always visible (discoverability); disabled without `routingmanager.read`. -->
        <a
          hlmBtn
          size="sm"
          variant="default"
          [class]="canOpenCallExplainer()
            ? 'shadow-sm ring-1 ring-primary/30 transition-shadow hover:shadow-md'
            : 'shadow-sm ring-1 ring-primary/40'"
          [hlmTooltip]="callExplainerTooltip()"
          [attr.title]="callExplainerTooltip()"
          [attr.aria-disabled]="!canOpenCallExplainer()"
          [attr.tabindex]="canOpenCallExplainer() ? 0 : -1"
          [class.cursor-not-allowed]="!canOpenCallExplainer()"
          [class.opacity-70]="!canOpenCallExplainer()"
          [href]="canOpenCallExplainer() ? callExplainerUrl() : null"
          target="_blank"
          rel="noopener noreferrer"
          (click)="onOpenCallExplainerClick($event)"
        >
          @if (canOpenCallExplainer()) {
            <hlm-icon name="lucideExternalLink" size="0.9rem" />
          } @else {
            <hlm-icon name="lucideLock" size="0.9rem" />
          }
          Explain Routing
        </a>
      </div>
      <p hlmDialogDescription class="mt-1">
        Accounting, Call Notes, Billing and Transcript for this call.
      </p>
    </div>

    <!--
      Header actions: Fullscreen + Close. The Share affordance moved onto the
      CCHSID pill in the heading row (EB-3807). Close stays icon-only as the
      conventional dismiss affordance.
    -->
    <div class="flex flex-none items-center gap-2 pt-1">
      <button
        hlmBtn variant="outline" size="sm"
        type="button"
        tabindex="-1"
        (click)="toggleFullscreen()"
        [attr.aria-label]="fullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'"
      >
        <hlm-icon name="lucideExpand" size="0.9rem" />
        {{ fullscreen() ? 'Exit fullscreen' : 'Fullscreen' }}
      </button>

      <button
        hlmBtn variant="ghost" size="icon"
        type="button"
        tabindex="-1"
        (click)="close()"
        aria-label="Close dialog"
        title="Close"
      >
        <hlm-icon name="lucideX" size="1rem" />
      </button>
    </div>
  </hlm-dialog-header>

  <section hlmDialogBody class="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
    <hlm-tabs [value]="activeTab()" (valueChange)="activeTab.set($any($event))" class="flex min-h-0 flex-1 flex-col">
      <hlm-tab value="details" label="Call Details">
        <cmt-call-note-details-tab
          [fetchFn]="fetchCallNoteDetails"
          [unavailableReason]="callDetailsUnavailable()"
          [durations]="data.durations ?? null"
          [callStatus]="data.summary?.status ?? null"
          [isBilled]="isBilled()"
          [customerId]="data.customerId ?? null"
          [callNoteId]="data.callNoteId"
          [fallbackAgentName]="data.summary?.agentName ?? null"
        />
      </hlm-tab>
      <hlm-tab value="cdr" label="Accounting / CDR Data">
        <div class="flex min-h-0 flex-1 flex-col gap-3">
          <!--
            CDR summary strip — mirrors the old Kundenverwaltung 'CDR' tab header
            (Datum / AnruferNr / Anruf angenommen / Anrufdauer / Gebucht). Pulled
            directly from the call-row summary so we don't refetch.
          -->
          @if (data.summary; as s) {
            <!--
              Combined Summary + Call-ID legend card. The summary keeps its 5-col
              grid; the legend is appended as a divided row below it (only when
              call IDs exist), so the dialog header stays a single visual block.
            -->
            <section class="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <dl class="grid gap-x-6 gap-y-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                <div class="flex flex-col">
                  <dt class="cmt-eyebrow">Started</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ formatBerlinTimestamp(s.timestamp) }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="cmt-eyebrow">Caller number</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ s.callerNumber || '—' }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="cmt-eyebrow">Answered</dt>
                  <dd class="text-base font-medium text-foreground">
                    @if (s.accepted === true) { Yes }
                    @else if (s.accepted === false) { No }
                    @else { — }
                  </dd>
                </div>
                <div class="flex flex-col">
                  <dt class="cmt-eyebrow">Talk duration</dt>
                  <dd class="font-mono-feat text-base text-foreground">{{ formatTalkDuration() }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="cmt-eyebrow">Billed</dt>
                  <dd class="text-base font-medium text-foreground">
                    @if (isBilled() === true) { Yes }
                    @else if (isBilled() === false) { No }
                    @else { — }
                  </dd>
                </div>
              </dl>

              @if (callIdLegend().length > 0) {
                <!-- Call-ID legend — same card, separated by a hairline so it
                     reads as a contiguous summary block. Each entry: letter
                     chip (matches table) + full ID (mono, text-base for
                     readability) + copy button. -->
                <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/70 pt-3">
                  <span class="cmt-eyebrow">Call IDs</span>
                  @for (entry of callIdLegend(); track entry.id) {
                    <span class="inline-flex items-center gap-2">
                      <span [class]="'cmt-callid-chip cmt-callid-hue-' + entry.hue">
                        <span class="font-semibold">{{ entry.letter }}</span>
                      </span>
                      <span class="font-mono-feat break-all text-base text-foreground" [attr.title]="entry.id">{{ entry.id }}</span>
                      <button
                        type="button"
                        class="cmt-copy-btn"
                        (click)="copyValue(entry.id, 'Call ID ' + entry.letter)"
                        [attr.aria-label]="'Copy Call ID ' + entry.letter"
                        hlmTooltip="Copy this Call ID"
                      >
                        <hlm-icon name="lucideCopy" size="0.85rem" />
                      </button>
                    </span>
                  }
                </div>
              }
            </section>
          }
          <cmt-call-details-tab
            class="flex min-h-0 flex-1 flex-col"
            tabLabel="Accounting / CDR Data"
            fileNameBase="cdr-accounting"
            [columnDefs]="accountingCols"
            [fetchFn]="fetchAccounting"
            [isFullWidthRow]="cdrIsFullWidthRow"
            [fullWidthCellRenderer]="cdrFullWidthCellRenderer"
            [cellRefreshKey]="agentRefreshKey()"
          >
            <!-- EB-3807: the Open Call Explainer button moved out of this toolbar
                 and into the dialog header (renamed "Explain Routing", next to
                 the CCHSID). -->
          </cmt-call-details-tab>
        </div>
      </hlm-tab>
      <hlm-tab value="notes" label="Call Notes">
        <div class="flex min-h-0 flex-1 flex-col gap-3">
          <!--
            Minimal context block above the table: just the Parent call note ID
            and the Subject(s). Anything else duplicates dialog header / Call
            Details tab content. When >1 distinct subject exists each subject
            gets its own colored letter chip — the same chip is rendered in the
            in-table Subject column so operators can correlate at a glance.
          -->
          @if (data.callNoteId !== null || subjectLegend().length > 0) {
            <section class="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
              @if (data.callNoteId !== null) {
                <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span class="cmt-eyebrow">Parent call note</span>
                  <span class="font-mono-feat text-base text-foreground" [attr.title]="data.callNoteId">{{ data.callNoteId }}</span>
                  <button
                    type="button"
                    class="cmt-copy-btn"
                    (click)="copyValue(data.callNoteId, 'Call note ID')"
                    aria-label="Copy call note ID"
                    hlmTooltip="Copy this call note ID"
                  >
                    <hlm-icon name="lucideCopy" size="0.85rem" />
                  </button>
                </div>
              }

              @if (subjectLegend(); as legend) {
                @if (legend.length === 1) {
                  <!-- Single subject — render bigger, set apart on its own
                       muted card-within-card so it reads as the main heading
                       of the table. -->
                  <div class="flex flex-col gap-1 border-t border-border/70 pt-3">
                    <span class="cmt-eyebrow">Subject</span>
                    <div class="rounded-lg bg-muted/40 px-3 py-2">
                      <span class="break-words text-lg leading-snug text-foreground">{{ legend[0].subject }}</span>
                    </div>
                  </div>
                } @else if (legend.length > 1) {
                  <div class="flex flex-col gap-2 border-t border-border/70 pt-3">
                    <span class="cmt-eyebrow">Subjects</span>
                    <div class="flex min-w-0 flex-col gap-1.5">
                      @for (entry of legend; track entry.subject) {
                        <!-- Each subject in its own muted tile — bigger text,
                             visually separated, hue-matched letter chip pinned
                             on the left. -->
                        <div class="flex min-w-0 items-start gap-3 rounded-lg bg-muted/40 px-3 py-2">
                          <span [class]="'cmt-callid-chip mt-0.5 shrink-0 cmt-callid-hue-' + entry.hue">
                            <span class="font-semibold">{{ entry.letter }}</span>
                          </span>
                          <span class="min-w-0 flex-1 break-words text-lg leading-snug text-foreground" [attr.title]="entry.subject">{{ entry.subject }}</span>
                        </div>
                      }
                    </div>
                  </div>
                }
              }
            </section>
          }
          <cmt-call-details-tab
            class="flex min-h-0 flex-1 flex-col"
            tabLabel="Call Notes"
            fileNameBase="call-notes"
            [columnDefs]="callNotesColumnDefs()"
            [fetchFn]="fetchCallNotes"
            [unavailableReason]="callNotesUnavailable()"
            [isFullWidthRow]="cdrIsFullWidthRow"
            [fullWidthCellRenderer]="cdrFullWidthCellRenderer"
            [rowHeight]="56"
          />
        </div>
      </hlm-tab>
      <hlm-tab value="billing" label="Billing">
        <div class="flex min-h-0 flex-1 flex-col gap-3">
          <!--
            Billing summary block — operators look at the TOTAL first ("did
            this call cost what we expect?"). The parent call note ID stays
            for cross-reference with the other tabs. Anything else duplicates
            the Call Details tab.
          -->
          @if (data.callNoteId !== null) {
            <section class="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span class="cmt-eyebrow">Total</span>
                <span class="font-mono-feat text-lg font-semibold text-foreground">{{ billingTotalLabel() }}</span>
              </div>
            </section>
          }
          <cmt-call-details-tab
            class="flex min-h-0 flex-1 flex-col"
            tabLabel="Billing"
            fileNameBase="billing"
            [columnDefs]="billingColumnDefs()"
            [fetchFn]="fetchBillings"
            [unavailableReason]="billingUnavailable()"
            [isFullWidthRow]="cdrIsFullWidthRow"
            [fullWidthCellRenderer]="cdrFullWidthCellRenderer"
          />
        </div>
      </hlm-tab>
      <hlm-tab value="transcript" label="Transcript">
        <div class="flex min-h-0 flex-1 flex-col gap-3">
          <!--
            Test-env disclaimer: the production transcription pipeline does not
            run against the test backend, so we render a sample German dialogue
            instead. The banner makes the simulation explicit so operators do
            not mistake mock content for a real call.
          -->
          @if (isMockTranscript()) {
            <section class="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4 shadow-sm">
              <hlm-icon name="lucideTriangleAlert" size="1.1rem" class="mt-0.5 flex-none text-warning" />
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <div class="text-base font-semibold text-foreground">Mocked transcript</div>
                <p class="text-sm text-foreground">
                  Transcripts are only generated in the live environment. To preview the layout we show a
                  sample German conversation here. Once the test transcription API is enabled, this banner
                  disappears and the real transcript renders in its place.
                </p>
              </div>
            </section>
          }

          @if (transcriptLoading() && transcriptEntries().length === 0) {
            <div class="mx-auto mt-10 flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">
              <div class="text-lg font-semibold text-foreground">Loading transcript…</div>
              <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
              </div>
            </div>
          } @else if (transcriptError(); as err) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
              <div class="flex flex-1 flex-col gap-2">
                <div>
                  Failed to load Transcript (HTTP {{ err.status }}).
                  @if (err.correlationId) {
                    <span class="font-mono-feat text-sm text-muted-foreground">correlation-id: {{ err.correlationId }}</span>
                  }
                </div>
                <div>
                  <button hlmBtn variant="outline" size="sm" type="button" (click)="loadTranscript()">
                    <hlm-icon name="lucideRefreshCw" size="0.9rem" />
                    Retry
                  </button>
                </div>
              </div>
            </hlm-alert>
          } @else if (transcriptEntries().length === 0) {
            <div class="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 px-6 py-10 text-center">
              <hlm-icon name="lucideInbox" size="1.6rem" class="text-muted-foreground/70" />
              <div class="flex flex-col gap-1">
                <div class="text-sm font-medium text-foreground">No transcript for this call</div>
                <div class="text-sm text-muted-foreground">Nothing was returned by the backend.</div>
              </div>
              @if (missingReportState() === 'reported') {
                <div class="flex items-center gap-2 text-sm text-foreground">
                  <hlm-icon name="lucideCircleCheck" size="1rem" class="text-success" />
                  You reported this transcript as missing. Thank you!
                </div>
              } @else if (missingReportState() === 'saved') {
                <div class="flex items-center gap-2 text-sm text-foreground">
                  <hlm-icon name="lucideCircleCheck" size="1rem" class="text-success" />
                  Feedback saved. You already sent feedback for this transcript.
                </div>
              } @else {
                <button
                  hlmBtn
                  variant="outline"
                  size="sm"
                  type="button"
                  [disabled]="missingReportState() === 'sending'"
                  (click)="reportMissingTranscript()"
                >
                  Report missing transcript
                </button>
                @if (missingReportState() === 'error') {
                  <div class="text-sm text-destructive">Could not send the report. Please try again.</div>
                }
              }
            </div>
          } @else {
            <!-- Toolbar — same shape as the table tabs: search + count + download -->
            <div class="flex flex-wrap items-center gap-3">
              <div class="relative min-w-0 flex-1">
                <hlm-icon
                  name="lucideSearch"
                  size="0.9rem"
                  class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  hlmInput
                  type="text"
                  class="w-full pl-9"
                  placeholder="Search transcript…"
                  [value]="transcriptSearch()"
                  (input)="onTranscriptSearch($event)"
                  aria-label="Filter transcript lines"
                />
              </div>
              <span class="font-mono-feat text-sm text-muted-foreground">
                {{ transcriptFiltered().length }} / {{ transcriptEntries().length }}
              </span>
              <button hlmBtn variant="outline" size="sm" type="button" (click)="exportTranscriptText()">
                <hlm-icon name="lucideDownload" size="0.9rem" />
                Download .txt
              </button>
            </div>

            <!--
              Chat surface. Speaker bubbles alternate sides (CALLER left,
              AGENT right) like a support-chat transcript. Each line carries:
                · avatar circle on the speaker side (icon + tone per role)
                · speaker name + timestamp · #idx in the meta row
                · bubble with the message, tail-rounded toward the avatar
              Layout adapts so caller is mirrored (avatar left, meta left-aligned)
              and agent is right-aligned (avatar right, meta right-aligned).
            -->
            <div class="min-h-[340px] flex-1 overflow-auto rounded-xl border border-border bg-card/40 p-4">
              <div class="mx-auto flex max-w-5xl flex-col gap-4">
                @for (entry of transcriptFiltered(); track entry.id) {
                  @let isAgent = actorSide(entry.speaker) === 'agent';

                  <!--
                    Two-column chat: each turn lives in a 75%-wide *group*
                    column, anchored to the speaker's side. The outer flex
                    row stays full-width; the inner group uses `mr-auto` to
                    pin to the left (caller) or `ml-auto` to pin to the
                    right (agent). Inside the group: [avatar][meta][bubble]
                    for caller, reversed via flex-row-reverse for agent so
                    avatar+meta hug the bubble on the speaker's side. This
                    gives a clear left/right channel — short bubbles still
                    sit on their respective side, long bubbles can fill the
                    group up to 75% before wrapping.
                  -->
                  <div class="flex w-full">
                    <div class="flex max-w-[78%] items-center gap-3"
                         [class.mr-auto]="!isAgent"
                         [class.ml-auto]="isAgent"
                         [class.flex-row-reverse]="isAgent">

                      <!-- Avatar circle (always on the speaker's side) -->
                      <div class="flex h-9 w-9 flex-none items-center justify-center rounded-full"
                           [class]="avatarToneClass(entry.speaker)">
                        <hlm-icon [name]="actorAvatarIcon(entry.speaker)" size="1rem" />
                      </div>

                      <!-- Meta (name + time + idx) — between avatar and bubble, on the speaker's side.
                           Outer text-sm so the time/idx row meets the playbook's "no text-xs in body/label"
                           rule; chip-style elements remain exempt elsewhere. -->
                      <div class="flex shrink-0 flex-col gap-0.5 text-sm leading-tight"
                           [class.items-start]="!isAgent"
                           [class.items-end]="isAgent">
                        <span class="font-semibold"
                              [class.text-primary]="isAgent && (entry.speaker === 'AGENT' || entry.speaker === 'EMPLOYEE' || entry.speaker === 'EBUERO')"
                              [class.text-accent-foreground]="isAgent && entry.speaker !== 'AGENT' && entry.speaker !== 'EMPLOYEE' && entry.speaker !== 'EBUERO'"
                              [class.text-ring-purple]="!isAgent">
                          {{ actorDisplayName(entry) }}
                        </span>
                        <div class="flex items-baseline gap-2 font-mono-feat text-muted-foreground">
                          @if (entry.timestamp; as ts) {
                            <span>{{ formatLineTime(ts) }}</span>
                          }
                          <span class="select-none text-muted-foreground/60">#{{ entry.id }}</span>
                        </div>
                      </div>

                      <!-- Bubble — fills remaining group space up to its natural content width -->
                      <div
                        class="min-w-0 border px-4 py-2.5 text-base leading-relaxed shadow-sm"
                        [class]="bubbleToneClass(entry.speaker)"
                      >
                        <span class="whitespace-pre-wrap break-words">{{ entry.text }}</span>
                      </div>
                    </div>
                  </div>
                }

                @if (transcriptFiltered().length === 0) {
                  <div class="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
                    <hlm-icon name="lucideSearch" size="1.2rem" class="text-muted-foreground/70" />
                    No lines match “{{ transcriptSearch() }}”.
                  </div>
                }
              </div>
            </div>
            @if (!isMockTranscript()) {
              <cmt-transcript-feedback-bar [cchsid]="data.cchsid"
                [callDirection]="callDirection()"
                [customerId]="data.customerId ?? null"
              />
            }
          }
        </div>
      </hlm-tab>
      <hlm-tab value="recording" label="Recording" testId="tab-recording">
        <div class="flex min-h-0 flex-1 flex-col gap-3">
          <!--
            AD-8: the tab is always rendered; only the PANEL locks. Nothing below
            runs without the permission — the fetch is gated too, so no TAPStore
            address reaches the DOM.
          -->
          @if (!canReadRecording()) {
            <section
              class="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4 shadow-sm"
              data-testid="recording-no-permission"
            >
              <hlm-icon name="lucideLock" size="1.1rem" class="mt-0.5 flex-none text-warning" />
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <div class="text-base font-semibold text-foreground">
                  You do not have permission to listen to recordings
                </div>
                <p class="text-sm text-foreground">
                  Playback requires the
                  <span class="font-medium">Call Recording access</span>
                  permission. Ask an administrator if you need it.
                </p>
              </div>
            </section>
          } @else if (recordingLoading() || recordingProbing()) {
            <div class="mx-auto mt-10 flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">
              <div class="text-lg font-semibold text-foreground">Loading recording…</div>
              <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
              </div>
            </div>
          } @else if (recordingError(); as err) {
            <!--
              The lookup failed — we do NOT know whether a recording exists, so
              this must not say "the call was not recorded" (AD-6).
            -->
            <hlm-alert tone="error" data-testid="recording-error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
              <div class="flex flex-1 flex-col gap-2">
                <div class="flex flex-col gap-1">
                  <!-- Status leads only when there is one: "HTTP 0" is not reportable. -->
                  @if (err.status) {
                    <div>Failed to load Recording (HTTP {{ err.status }}).</div>
                    <div class="text-sm text-muted-foreground">{{ err.message }}</div>
                  } @else {
                    <div>{{ err.message }}</div>
                  }
                </div>
                <div>
                  <button hlmBtn variant="outline" size="sm" type="button" (click)="loadRecording()">
                    <hlm-icon name="lucideRefreshCw" size="0.9rem" />
                    Retry
                  </button>
                </div>
              </div>
            </hlm-alert>
          }
          @if (canReadRecording() && recordingUrl(); as url) {
            <!--
              Mounted so `preload="metadata"` can probe, hidden until the
              browser confirms the file is there — otherwise a call with no
              recording flashes a play bar first.

              Class toggle, never @if, for the probe→reveal transition: an @if
              here would remount the element and restart the download. It does
              NOT survive a tab switch — hlm-tab renders its content under its
              own @if, so leaving the tab tears the player down either way and
              playback position is lost.
            -->
            <section
              class="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm"
              [class.hidden]="!recordingReady()"
              data-testid="recording-player"
            >
              <header class="flex flex-wrap items-center gap-2.5">
                <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                  <hlm-icon name="lucideHeadphones" size="1rem" />
                </span>
                <h2 class="font-display text-lg font-semibold text-foreground">Recording</h2>
              </header>
              <audio
                class="w-full"
                controls
                preload="metadata"
                controlsList="nodownload"
                [src]="url"
                (loadedmetadata)="onRecordingLoadedMetadata()"
                (error)="onRecordingLoadError($event)"
              ></audio>
              <p class="text-sm text-muted-foreground">Recordings are kept for about a month.</p>
            </section>
          }
          <!--
            Keyed on `recordingUnavailable`, not "no URL" — mid-lookup there
            is no URL either, so an @else would stack the empty state under
            the loading bar.
          -->
          @if (canReadRecording() && recordingUnavailable()) {
            <!--
              AD-6: one message for every cause of a MISSING recording — a
              failed lookup is an error alert, not this. Retention is the
              dominant cause and is covered by the copy, not a branch.
            -->
            <div
              class="flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/50 px-6 py-10 text-center"
              data-testid="recording-empty"
            >
              <hlm-icon name="lucideMicOff" size="1.6rem" class="text-muted-foreground/70" />
              <div class="flex flex-col gap-1">
                <div class="text-base font-medium text-foreground">No recording for this call</div>
                <div class="text-sm text-muted-foreground">
                  The call was not recorded, or the recording is no longer stored.
                </div>
                <div class="text-sm text-muted-foreground">Recordings are kept for about a month.</div>
              </div>
              <button hlmBtn variant="outline" size="sm" type="button" (click)="loadRecording()">
                <hlm-icon name="lucideRefreshCw" size="0.9rem" />
                Retry
              </button>
            </div>
          }
        </div>
      </hlm-tab>
    </hlm-tabs>
  </section>
</hlm-dialog-surface>
